import { useLocation, useNavigate } from 'react-router-dom';
import {
  IconCheck,
  IconCircleCheckFilled,
  IconCircleXFilled,
  IconHome,
  IconUser,
  IconCamera,
} from '@tabler/icons-react';
import { Button } from '../../components/common/Button';
import { Card } from '../../components/common/Card';
import { ExamPortalHeader } from './components/ExamPortalHeader';
import classes from './css/ExamSubmittedPage.module.css';
import { getRegistrationId, getRollNo } from '../../services/authSession';
import Footer from '../../components/layout/Footer';
import type { ExamQuestionMatchPair, ExamQuestionOption } from '../../services/sampleExamTypes';
import { normalizeToken } from './utils/matchPairAnswer';

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

type SampleExamMcqQuestionResult = {
  uiType: 'single';
  questionId: number;
  questionNo: number;
  questionText: string;
  options: ExamQuestionOption[];
  selectedOptionId: string | null;
  correctOptionId: string | null;
  isCorrect: boolean;
  scoredMarks: number;
  totalMarks: number;
};

type SampleExamMatchPairQuestionResult = {
  uiType: 'match_pair';
  questionId: number;
  questionNo: number;
  questionText: string;
  matchPairs: ExamQuestionMatchPair[];
  selectedPairs: Record<string, string>;
  correctPairs: Record<string, string>;
  isCorrect: boolean;
  scoredMarks: number;
  totalMarks: number;
};

type SampleExamQuestionResult = SampleExamMcqQuestionResult | SampleExamMatchPairQuestionResult;

function correctMatchForLeft(
  left: string,
  correctPairs: Record<string, string>,
  matchPairs: ExamQuestionMatchPair[]
): string {
  const fromMap = correctPairs[left];
  if (fromMap != null && String(fromMap).trim() !== '') return String(fromMap).trim();
  const row = matchPairs.find((p) => String(p.leftText ?? '').trim() === left);
  return row ? String(row.rightText ?? '').trim() : '';
}

type SampleExamResultPayload = {
  totalQuestions: number;
  score: number;
  maxMarks: number;
  questionResults: SampleExamQuestionResult[];
};

export interface ExamSubmittedState {
  userName?: string;
  userNameHindi?: string;
  rollNumber?: string;
  submittedAt?: string;
  screenshot?: string;
  result?: SampleExamResultPayload;
}

function formatSubmittedAt(): string {
  const now = new Date();
  const d = now.getDate().toString().padStart(2, '0');
  const m = (now.getMonth() + 1).toString().padStart(2, '0');
  const y = now.getFullYear();
  const h = now.getHours().toString().padStart(2, '0');
  const min = now.getMinutes().toString().padStart(2, '0');
  const s = now.getSeconds().toString().padStart(2, '0');
  return `${d}/${m}/${y} ${h}:${min}:${s}`;
}

export default function ExamSubmittedPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as ExamSubmittedState | null) ?? {};
  const userName = state.userName ?? 'Student';
  const userNameHindi = state.userNameHindi ?? '';
  const fromState = state.rollNumber?.trim();
  const rollFromState = fromState && fromState !== '—' ? fromState : '';
  const rollNumber = rollFromState || getRollNo().trim() || getRegistrationId().trim() || '—';
  const result = state.result;

  const getOptionLetter = (options: ExamQuestionOption[], id: string | null) => {
    if (!id) return null;
    const idx = options.findIndex((o) => String(o.optionId) === id);
    return idx >= 0 ? LETTERS[idx] : null;
  };

  return (
    <>
      <ExamPortalHeader />
      <div className={classes.page}>
        <Card className={classes.container} shadow="sm" radius="md">
          {result ? (
            <>
              <Card className={classes.userCard} shadow="xs" radius="md">
                <div className={classes.userInfoRow}>
                  <div className={classes.avatar}>{String(userName).trim().charAt(0) || 'S'}</div>
                  <div className={classes.userInfoContent}>
                    <div className={classes.userName}>{userName}</div>
                    {userNameHindi ? (
                      <div className={classes.userNameHindi}>{userNameHindi}</div>
                    ) : null}
                    <div className={classes.rollText}>Roll No: {rollNumber}</div>
                  </div>
                </div>
              </Card>

              <Card className={classes.mainScoreCard} shadow="xs" radius="md">
                <div className={classes.mainScoreValue}>
                  {result.score}/{result.maxMarks}
                </div>
                <div className={classes.mainScoreText}>
                  You scored {result.score} out of {result.maxMarks} marks
                </div>
              </Card>

              <div className={classes.resultList}>
                {result.questionResults.map((q) => (
                  <Card
                    key={q.questionId}
                    className={`${classes.questionCard} ${q.isCorrect ? classes.cardCorrect : classes.cardWrong}`}
                    shadow="xs"
                    radius="md"
                  >
                    <div className={classes.questionTitleRow}>
                      {q.isCorrect ? (
                        <IconCircleCheckFilled
                          size={18}
                          className={classes.correctIcon}
                          aria-hidden
                        />
                      ) : (
                        <IconCircleXFilled size={18} className={classes.wrongIcon} aria-hidden />
                      )}
                      <div className={classes.questionText}>
                        Q{q.questionNo}. {q.questionText}
                      </div>
                      <div className={classes.questionMarks}>
                        Marks: {q.scoredMarks}/{q.totalMarks}
                      </div>
                    </div>

                    {q.uiType === 'match_pair' ? (
                      <>
                        <div className={classes.matchPairResults}>
                          {q.matchPairs.map((pair) => {
                            const left = String(pair.leftText ?? '').trim();
                            const selected = String(q.selectedPairs[left] ?? '').trim();
                            const correct = correctMatchForLeft(left, q.correctPairs, q.matchPairs);
                            const rowOk =
                              selected !== '' &&
                              normalizeToken(selected) === normalizeToken(correct) &&
                              correct !== '';
                            const rowClass = `${classes.matchPairRow} ${
                              rowOk ? classes.matchPairRowOk : classes.matchPairRowBad
                            }`;
                            return (
                              <div key={pair.pairId} className={rowClass}>
                                <div className={classes.matchPairLeftBlock}>
                                  <span className={classes.matchPairLeft}>{left}</span>
                                  <span className={classes.matchPairArrow} aria-hidden>
                                    →
                                  </span>
                                </div>
                                <div className={classes.matchPairYour}>
                                  <span className={classes.matchPairLabel}>Your match</span>
                                  <span className={classes.matchPairValue}>{selected || '—'}</span>
                                </div>
                                {!rowOk && correct ? (
                                  <div className={classes.matchPairCorrectCell}>
                                    <span className={classes.matchPairLabel}>Correct</span>
                                    <span className={classes.matchPairValue}>{correct}</span>
                                  </div>
                                ) : null}
                              </div>
                            );
                          })}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className={classes.optionsGrid}>
                          {q.options.map((opt, idx) => {
                            const optionId = String(opt.optionId);
                            const isSelected = q.selectedOptionId === optionId;
                            const isCorrectOpt = q.correctOptionId === optionId;
                            const className = `${classes.optionRow} ${
                              isCorrectOpt
                                ? classes.optionCorrect
                                : isSelected && !q.isCorrect
                                  ? classes.optionWrong
                                  : ''
                            }`;
                            return (
                              <div key={optionId} className={className}>
                                <span className={classes.optionLetter}>{LETTERS[idx]}</span>
                                <span>{opt.optionText}</span>
                              </div>
                            );
                          })}
                        </div>

                        {!q.isCorrect && q.correctOptionId ? (
                          <div className={classes.correctAnswerText}>
                            Correct Answer:{' '}
                            {(() => {
                              const correctLetter = getOptionLetter(q.options, q.correctOptionId);
                              const correctText =
                                q.options.find((o) => String(o.optionId) === q.correctOptionId)
                                  ?.optionText ?? '';
                              return correctLetter
                                ? `${correctLetter}. ${correctText}`
                                : correctText;
                            })()}
                          </div>
                        ) : null}
                      </>
                    )}
                  </Card>
                ))}
              </div>

              <Button className={classes.dashboardBtn} onClick={() => navigate('/dashboard')}>
                <IconHome size={20} aria-hidden />
                Go to Dashboard
              </Button>
            </>
          ) : (
            <>
              <div className={classes.successIconWrap}>
                <IconCheck size={48} strokeWidth={3} aria-hidden />
              </div>
              <h1 className={classes.title}>Exam Submitted Successfully!</h1>

              <Card className={classes.detailsCard} shadow="sm" radius="md">
                <div className={classes.cardRow}>
                  <IconUser size={20} className={classes.cardIcon} aria-hidden />
                  <div className={classes.cardContent}>
                    <span className={classes.cardLabel}>Name</span>
                    <span className={classes.cardValue}>{userName}</span>
                    {userNameHindi && (
                      <span className={classes.cardValueHindi}>{userNameHindi}</span>
                    )}
                  </div>
                </div>
                <div className={classes.cardDivider} />
                <div className={classes.cardRow}>
                  <div className={classes.cardContent}>
                    <span className={classes.cardLabel}>Roll Number</span>
                    <span className={classes.cardValue}>{rollNumber}</span>
                  </div>
                </div>
              </Card>

              <p className={classes.confirmText}>Your exam has been submitted successfully.</p>
              <p className={classes.resultsText}>
                Submitted at: {state.submittedAt ?? formatSubmittedAt()}
              </p>

              <Card className={classes.screenshotCard} shadow="xs" radius="md">
                {state.screenshot ? (
                  <div className={classes.screenshotActualWrap}>
                    <img
                      src={state.screenshot}
                      alt="Submission Confirmation"
                      className={classes.screenshotImage}
                    />
                    <div className={classes.screenshotCaption}>
                      <IconCamera size={16} aria-hidden />
                      <span>Captured at submission</span>
                    </div>
                  </div>
                ) : (
                  <div className={classes.screenshotBox}>
                    <IconCamera size={20} className={classes.screenshotIcon} aria-hidden />
                    <span>Please take a screenshot of this page for your records.</span>
                  </div>
                )}
              </Card>

              <p className={classes.resultsText}>
                Results will be announced later through official channels.
              </p>

              <Button
                className={classes.dashboardBtn}
                onClick={() => navigate('/dashboard')}
                aria-label="Go to Dashboard"
              >
                <IconHome size={22} aria-hidden />
                Go to Dashboard
              </Button>
            </>
          )}
        </Card>
      </div>
      <Footer />
    </>
  );
}
